import * as z from "zod";

import { UNIT_KINDS, type GameEvent, type GameState, type UnitKind } from "../engine/types";

export const GameAchievementProgressSchema = z.object({
  trackingValid: z.boolean(),
  builtUnitKinds: z.array(z.enum(UNIT_KINDS)),
  unitsLost: z.number().int().nonnegative(),
  unitsKilled: z.number().int().nonnegative().default(0),
  hasCaptured: z.boolean().default(false),
  hasRepaired: z.boolean().default(false),
  startedWithAirport: z.boolean().default(false),
});

export type GameAchievementProgress = z.infer<typeof GameAchievementProgressSchema>;

function playerStartsWithAirport(state: Pick<GameState, "tiles">): boolean {
  return state.tiles.some((tile) => tile.kind === "airport" && tile.owner === "p1");
}

export function createGameAchievementProgress(
  state?: Pick<GameState, "tiles">,
): GameAchievementProgress {
  return {
    trackingValid: true,
    builtUnitKinds: [],
    unitsLost: 0,
    unitsKilled: 0,
    hasCaptured: false,
    hasRepaired: false,
    startedWithAirport: state ? playerStartsWithAirport(state) : false,
  };
}

export function createUntrackedGameAchievementProgress(): GameAchievementProgress {
  return {
    trackingValid: false,
    builtUnitKinds: [],
    unitsLost: 0,
    unitsKilled: 0,
    hasCaptured: false,
    hasRepaired: false,
    startedWithAirport: false,
  };
}

export function resolveGameAchievementProgress({
  stored,
  state,
}: {
  stored: unknown;
  state: Pick<GameState, "tiles" | "currentDay" | "status">;
}): GameAchievementProgress {
  if (stored !== undefined && stored !== null) {
    return GameAchievementProgressSchema.parse(stored);
  }
  if (state.status === "in_progress" && state.currentDay === 1) {
    return createGameAchievementProgress(state);
  }
  return createUntrackedGameAchievementProgress();
}

function playerLostUnit(event: GameEvent): boolean {
  if (event.type === "cannonFired") {
    return event.playerId === "p2" && event.killed;
  }
  if (event.type === "attacked") {
    return event.playerId === "p2" && event.killed && event.targetKind !== "cannon";
  }
  return event.type === "countered" && event.playerId === "p2" && event.killed;
}

function playerKilledUnit(event: GameEvent): boolean {
  if (event.type === "attacked") {
    return event.playerId === "p1" && event.killed && event.targetKind !== "cannon";
  }
  return event.type === "countered" && event.playerId === "p1" && event.killed;
}

export function foldGameAchievementProgress({
  progress,
  events,
}: {
  progress: GameAchievementProgress;
  events: readonly GameEvent[];
}): GameAchievementProgress {
  const builtKinds = new Set<UnitKind>(progress.builtUnitKinds);
  let unitsLost = progress.unitsLost;
  let unitsKilled = progress.unitsKilled;
  let hasCaptured = progress.hasCaptured;
  let hasRepaired = progress.hasRepaired;

  for (const event of events) {
    if (event.type === "unitBuilt" && event.playerId === "p1") {
      builtKinds.add(event.kind);
    }
    if (playerLostUnit(event)) {
      unitsLost += 1;
    }
    if (playerKilledUnit(event)) {
      unitsKilled += 1;
    }
    if (event.type === "captured" && event.playerId === "p1") {
      hasCaptured = true;
    }
    if (event.type === "unitRepaired" && event.playerId === "p1") {
      hasRepaired = true;
    }
  }

  return {
    ...progress,
    builtUnitKinds: [...builtKinds],
    unitsLost,
    unitsKilled,
    hasCaptured,
    hasRepaired,
  };
}
